import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getLadgerDataWithOrg } from '../utils/api/LedgerAPI';
import { convertToIndianTimeZone } from '../utils/utils';
import { Icon } from '@iconify/react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const TableDataLayer = () => {
    const user = useSelector((state) => state?.user?.user);
    const [ledgerData, setLedgerData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [limit] = useState(50);
    const [totalRecords, setTotalRecords] = useState(0);

    const [filters, setFilters] = useState({
        type: 'all',
        source: 'all',
        dateRange: [null, null],
    });

    const [tempType, setTempType] = useState('all');
    const [tempSource, setTempSource] = useState('all');
    const [tempDateRange, setTempDateRange] = useState([null, null]);

    const formatDate = (date) => date?.toISOString().split('T')[0];

    const updateURLParams = (newFilters, newPage = 1) => {
        const url = new URL(window.location);
        url.searchParams.set('type', newFilters.type);
        url.searchParams.set('source', newFilters.source);
        if (newFilters.dateRange[0])
            url.searchParams.set('start', formatDate(newFilters.dateRange[0]));
        else url.searchParams.delete('start');

        if (newFilters.dateRange[1])
            url.searchParams.set('end', formatDate(newFilters.dateRange[1]));
        else url.searchParams.delete('end');

        url.searchParams.set('page', newPage);
        window.history.replaceState({}, '', url);
    };

    const fetchLedger = () => {
        if (!user?.organizationId) return;

        setIsLoading(true);

        getLadgerDataWithOrg({
            organizationId: user.organizationId,
            page,
            limit,
            type: filters.type === 'all' ? '' : filters.type,
            source: filters.source === 'all' ? '' : filters.source,
            startedAt: filters.dateRange[0] ? formatDate(filters.dateRange[0]) : '',
            endedAt: filters.dateRange[1] ? formatDate(filters.dateRange[1]) : '',
        })
            .then((data) => {
                setLedgerData(data?.ledger || []);
                setTotalRecords(data?.total || 0);
            })
            .finally(() => setIsLoading(false));
    };
    useEffect(() => {
        fetchLedger();
    }, [user, page, filters]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const type = params.get('type') || 'all';
        const source = params.get('source') || 'all';
        const start = params.get('start') ? new Date(params.get('start')) : null;
        const end = params.get('end') ? new Date(params.get('end')) : null;
        const urlPage = parseInt(params.get('page')) || 1;

        setFilters({ type, source, dateRange: [start, end] });
        setTempType(type);
        setTempSource(source);
        setTempDateRange([start, end]);
        setPage(urlPage);
    }, []);

    const totalPages = Math.ceil(totalRecords / limit);

    const applyFilters = () => {
        const newFilters = {
            type: tempType,
            source: tempSource,
            dateRange: tempDateRange,
        };
        setFilters(newFilters);
        setPage(1);
        updateURLParams(newFilters, 1);
    };

    return (
        <div className="card p-0 radius-12 w-100 h-100">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex flex-column gap-3">
                <div className="fw-semibold fs-5">Ledger History</div>

                <div className="d-flex flex-wrap gap-3 align-items-end justify-content-between">
                    <div className="d-flex flex-column flex-1">
                        <label className="text-sm text-muted mb-1">Transaction Type</label>
                        <select
                            value={tempType}
                            onChange={(e) => setTempType(e.target.value)}
                            style={{
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                padding: '8px 12px',
                                minWidth: '140px',
                            }}
                        >
                            <option value="all">All</option>
                            <option value="credit">Credit</option>
                            <option value="debit">Debit</option>
                        </select>
                    </div>

                    <div className="d-flex flex-column flex-1">
                        <label className="text-sm text-muted mb-1">Transaction Source</label>
                        <select
                            value={tempSource}
                            onChange={(e) => setTempSource(e.target.value)}
                            style={{
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                padding: '8px 12px',
                                minWidth: '140px',
                            }}
                        >
                            <option value="all">All</option>
                            <option value="purchase">Purchase</option>
                            <option value="call">Call</option>
                            <option value="grace">Grace</option>
                            <option value="promo">Promo</option>
                            <option value="manual">Manual</option>
                            <option value="chat">Chat</option>
                        </select>
                    </div>

                    <div className="d-flex flex-column " >
                        <label className="text-sm text-muted mb-1">Date Range</label>
                        <DatePicker
                            selectsRange
                            startDate={tempDateRange[0]}
                            endDate={tempDateRange[1]}
                            onChange={(update) => setTempDateRange(update)}
                      
                            placeholderText="Select date range"
                            className="datepicker_custom_form-control"
                            
                            maxDate={new Date()}
                            popperPlacement="bottom-start"
                            calendarClassName="custom-datepicker"
                        />
                    </div>

                    <button
                        className="btn btn-primary btn-sm radius-8 d-flex align-items-center " style={{
                            height: '47px'
                        }}
                        onClick={applyFilters}
                    >
                        <Icon icon="ic:baseline-filter-alt" className="me-1" />
                        Apply Filter
                    </button>
                </div>

            </div>

            <div className="card-body px-24 pt-10 pb-0">
                <div className="table-responsive">
                    <table className="table bordered-table sm-table mb-0">
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Source</th>
                                <th>Duration</th>
                                <th>Description</th>
                                <th>Time Stamp</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <tr key={i}>
                                        {Array.from({ length: 5 }).map((_, j) => (
                                            <td key={j}>
                                                <div className="shimmer shimmer-text" />
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : ledgerData.length > 0 ? (
                                ledgerData.map((entry) => (
                                    <tr key={entry.id}>
                                        <td>{entry.type || '-'}</td>
                                        <td>{entry.source || '-'}</td>
                                        <td>
                                            {typeof entry.seconds === 'number' && !isNaN(entry.seconds)
                                                ? (() => {
                                                    const minutes = Math.floor(entry.seconds / 60);
                                                    const seconds = entry.seconds % 60;
                                                    if (minutes > 0 && seconds > 0) return `${minutes}m ${seconds}s`;
                                                    if (minutes > 0) return `${minutes}m`;
                                                    return `${seconds}s`;
                                                })()
                                                : '-'}
                                        </td>

                                        <td>{entry.description || '-'}</td>
                                        <td>{convertToIndianTimeZone(entry.createdAt)}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5}>
                                        <div
                                            className="d-flex flex-column justify-content-center align-items-center"
                                            style={{ height: '40vh' }}
                                        >
                                            <Icon
                                                icon="icon-park-outline:file-search"
                                                className="text-3xl mb-3 text-muted"
                                            />
                                            <p className="text-secondary-light text-md mb-0">
                                                No ledger records found
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {ledgerData.length > 0 && (
                    <div className="d-flex justify-content-between align-items-center py-20 flex-wrap gap-2">
                        <span className="text-md text-muted">
                            Showing {(page - 1) * limit + 1} to{' '}
                            {Math.min(page * limit, totalRecords)} of {totalRecords} entries
                        </span>
                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-sm radius-8"
                                disabled={page === 1}
                                onClick={() => {
                                    const newPage = Math.max(page - 1, 1);
                                    setPage(newPage);
                                    updateURLParams(filters, newPage);
                                }}
                            >
                                <Icon icon="ep:arrow-left-bold" /> Prev
                            </button>
                            <button
                                className="btn btn-sm radius-8"
                                disabled={page === totalPages}
                                onClick={() => {
                                    const newPage = Math.min(page + 1, totalPages);
                                    setPage(newPage);
                                    updateURLParams(filters, newPage);
                                }}
                            >
                                Next <Icon icon="ep:arrow-right-bold" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
            
        </div>
    );
};

export default TableDataLayer;
